import { FetchQueryFetchPolicy, OperationType, getRequest } from 'relay-runtime'
import { GraphQLTaggedNode } from 'react-relay'
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { trackError } from 'services/analytics'
import { useFetchKey, useNodes } from 'hooks/relay'
import { useLogger } from 'hooks/logger'
import { useDebouncedCallback } from 'use-debounce'
import { IAutoCompleteOption } from 'components/AutoCompleteTextField/types'
import { arrayFilterNonNullable } from 'services/array'
import { Nullable } from 'types'
import { fetchQuery } from 'api'
import { useDetectChange } from './state'

/**
 * This hook is for using a GraphQL Query to update search results as the user types
 * It's meant to be used on Select fields, Autocomplete fields, and similar situation where the user types and
 * results are updated in real time.
 *
 * TODO: figure out how to get P to be typed correctly in consumer code
 *
 * @param query the query to execute
 * @param variables variables for the query
 * @param extractResults function to extract result array from query result
 * @param options optional extra configuration such as fetchPolicy and debounce delay
 */
export function useSearch<T extends OperationType, P extends {}>(
  query: GraphQLTaggedNode,
  variables: T['variables'],
  extractResults: (data: T['response']) => Nullable<(readonly Nullable<P>[])>,
  options: IUseSearchOptions<T> = {},
): IUseSearchResults<P> {
  const logger = useLogger('useSearch', getRequest(query).operation.name)
  const [results, setResults] = useState<P[]>([])
  const [loadingResults, setLoadingResults] = useState(false)
  const fetchKey = useRef<number>(0)
  const [isPending, startTransition] = useTransition()
  const resultList = useNodes(results)
  const enabled = options?.enabled ?? true
  const [cursor, setCursor] = useState<string | null>(null)
  const variablesDebounced = useDetectChange(variables)

  // TODO: with startTransition, it should be possible to rewrite this using relay hooks and to use store-and-network
  const fetch = useCallback(async (nextPage = false) => {
    const myKey = fetchKey.current + 1
    fetchKey.current = myKey
    logger.debug('Fetching using key', myKey)

    startTransition(() => {
      setLoadingResults(true)
    })

    try {
      const variablesWithCursor = nextPage && cursor ? { ...variablesDebounced, after: cursor } : variablesDebounced
      logger.debug('Fetching', variablesWithCursor)

      const data = await fetchQuery<T>(query, variablesWithCursor, {
        fetchPolicy: options?.fetchPolicy || 'store-or-network',
      })

      startTransition(() => {
        const newResults = (extractResults(data) || []).filter(arrayFilterNonNullable)
        setCursor(options?.extractCursor ? (options.extractCursor(data) || null) : null)
        logger.debug('New Results', newResults)
        logger.debug('Results were fetched using key', myKey, 'but current key is', fetchKey.current)

        if (fetchKey.current === myKey) {
          if (nextPage) {
            logger.debug('New results are being added at the bottom of the list (nextPage is true)')
            setResults(prevResults => [...prevResults, ...newResults].filter(arrayFilterNonNullable))
          } else {
            logger.debug('New results are replacing the list (nextPage is false)')
            setResults(newResults.filter(arrayFilterNonNullable))
          }
        } else {
          logger.debug('Skip setting results: fetchKey changed')
        }

        setLoadingResults(false)
      })
    } catch (error) {
      startTransition(() => {
        setLoadingResults(false)
      })

      trackError(error)
    }
  }, [cursor, variablesDebounced, fetchKey])

  const fetchDebounced = useDebouncedCallback(fetch, options?.delay, {
    leading: false, // Note: keep this off, of else it will cause duplicate results
  })

  useEffect(() => {
    if (enabled) fetchDebounced()?.catch(err => trackError(err))
  }, [variablesDebounced, options?.fetchKey, enabled])

  const loading = loadingResults || isPending

  return useMemo(() => {
    const loadMore = async () => {
      if (enabled && cursor) {
        return fetchDebounced(true)?.catch(err => trackError(err)) as Promise<void>
      }

      return undefined
    }

    return {
      results: enabled ? resultList : [],
      cursor: enabled ? cursor : undefined,
      loading: enabled ? loading : false,
      loadMore: cursor ? loadMore : undefined,
    }
  }, [enabled, resultList, cursor, loading])
}

function getOptionsFilter(results: IAutoCompleteOption[], search: string) {
  const searchTrimmed = search ? search.trim() : null

  return (options: IAutoCompleteOption[]) => {
    return options.filter((option) => {
      const matchesSearch = !searchTrimmed || option.label.toLowerCase().includes(searchTrimmed.toLowerCase())
      const isInResults = results.find(result => result.value === option.value)
      return matchesSearch && isInResults
    })
  }
}

/**
 * This hook is for using a GraphQL Query to generate props to pass to AutoCompleteTextField,
 * meaning an autocomplete situation where a single option is picked in the field.
 * This hook builds on top of useSearch which is a more generic search hook.
 *
 * TODO: figure out how to get P to be typed correctly in consumer code (same issue as useSearch)
 *
 * @param query the query to execute
 * @param searchToVariables function to map search text to variables for the query
 * @param extractResults function to extract result array from query result
 * @param options optional extra configuration such as fetchPolicy and debounce delay
 */
export function useSearchAutocomplete<T extends OperationType, OptionType = string>(
  query: GraphQLTaggedNode,
  searchToVariables: (text: string) => T['variables'],
  extractResults: (data: T['response']) => readonly IAutoCompleteOption<OptionType>[],
  options: IUseSearchOptions<T> = {},
) {
  const logger = useLogger('useSearchAutocomplete')
  const [fetchKey, incrementFetchKey] = useFetchKey()
  const [search, setSearch] = useState('')
  const variables = searchToVariables(search || '')
  const combinedOptions = {
    delay: 400,
    ...options,
    fetchKey,
  }
  const searchResults = useSearch(query, variables, extractResults, combinedOptions)

  // This will make options be preloaded on mount and update when the options fetchKey changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { incrementFetchKey() }, [options?.fetchKey])

  return useMemo(() => ({
    onInputChange: (event: SyntheticEvent, val: string) => {
      logger.debug('onInputChange', val)
      setSearch(val)
    },
    search,
    options: searchResults.results,
    loadMore: searchResults.loadMore,
    loading: searchResults.loading,
    // this is needed to prevent the default filtering of the autocomplete component, see MUI docs
    // TODO: fix any cast
    filterOptions: getOptionsFilter(searchResults.results as any, search) as any,
  }), [searchResults, search])
}

/**
 * This hook is for using a GraphQL Query to generate props to pass to AutoCompleteChipInput,
 * meaning an autocomplete situation where multiple options can be picked.
 * This hook builds on top of useSearch which is a more generic search hook.
 *
 * TODO: figure out how to get P to be typed correctly in consumer code (same issue as useSearch)
 *
 * @param query the query to execute
 * @param searchToVariables function to map search text to variables for the query
 * @param extractResults function to extract result array from query result
 * @param options optional extra configuration such as fetchPolicy and debounce delay
 */
export function useSearchAutocompleteMulti<T extends OperationType, OptionType = string>(
  query: GraphQLTaggedNode,
  searchToVariables: (text: string) => T['variables'],
  extractResults: (data: T['response']) => readonly IAutoCompleteOption<OptionType>[],
  options: IUseSearchOptions<T> = {},
) {
  const autocomplete = useSearchAutocomplete(
    query,
    searchToVariables,
    extractResults,
    options,
  )

  return useMemo(() => ({
    onInputChange: autocomplete.onInputChange,
    suggestions: autocomplete.options,
    loadMore: autocomplete.loadMore,
    loading: autocomplete.loading,
    suggestionsFormat: { text: 'label', value: 'value' },
    filterOptions: autocomplete.filterOptions,
  }), [autocomplete])
}

export function useListBoxScrollListener(callback: () => void) {
  return useCallback((event: SyntheticEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget

    const position = listboxNode.scrollTop + listboxNode.clientHeight
    if (typeof callback === 'function' && listboxNode.scrollHeight - position <= 1) {
      callback()
    }
  }, [callback])
}

/**
 * Use this to power our AutoCompleteTextField or AutoCompleteChipInput components when the search is happening
 * on the frontend. Do NOT use this to search over data from an API. Use useSearchAutocomplete for that.
 *
 * @param searchFunction the function to search for results
 */
export function useLocalAutocomplete(searchFunction: (text: string) => IAutoCompleteOption[] | Promise<IAutoCompleteOption[]>) {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<IAutoCompleteOption[]>([])
  const logger = useLogger('useLocalAutocomplete')

  const callback = async (event: any, value: any) => {
    setLoading(true)
    try {
      const newOptions = await searchFunction(value)
      logger.debug('newOptions', newOptions)
      setOptions(newOptions)
    } catch (error) {
      trackError(error)
    }
    setLoading(false)
  }

  const onInputChange = useDebouncedCallback(callback, 200, { leading: true })

  // Preload options on mount
  useEffect(() => { callback(null, '') }, [])

  return useMemo(() => ({
    onInputChange,
    options,
    loading,
    // this is needed to prevent the default filtering of the autocomplete component, see MUI docs
    filterOptions: (x: any) => x,
  }), [options, loading, onInputChange])
}

export interface IUseSearchOptions<T extends OperationType> {
  fetchPolicy?: FetchQueryFetchPolicy
  delay?: number
  fetchKey?: string | number
  enabled?: boolean
  extractCursor?: (data: T['response']) => Nullable<string>
}

export interface IUseSearchResults<P extends {}> {
  results: P[]
  cursor: Nullable<string>
  loading: boolean
  loadMore: (() => Promise<void>) | undefined
}
